export const MAX_CONTEXTS_PER_OWNER = 10;
export const MAX_SESSIONS_PER_CONTEXT = 100;

export type BrowserContextStatus = "ready" | "syncing" | "error";
export type ContextBrowser = "chrome" | "firefox";
export type ContextVaultStatus = "pending" | "ready" | "error";

export interface BrowserContextSummary {
  id: string;
  name: string;
  browser: ContextBrowser;
  current_revision: number;
  profile_size_bytes: number;
  status: BrowserContextStatus;
  created_at: string;
  updated_at: string;
  session_count: number;
  active_session_count: number;
  last_saved_at: string | null;
  vault_status?: ContextVaultStatus;
  vault_error?: string | null;
  vault_retryable?: boolean;
}

export interface CreateBrowserContextInput {
  name: string;
  /** Legacy caller hint only; the new UI has no context browser selector. */
  browser?: ContextBrowser;
}

export interface RenameBrowserContextInput {
  name: string;
}

export interface CommitProfileResponse {
  session_id: string;
  context_id: string;
  revision: number;
  checksum: string;
  size_bytes: number;
  idempotent: boolean;
}

export interface ProfileRevisionConflict {
  candidate_id: string;
  base_revision: number;
  current_revision: number;
}

export type CommitProfileResult =
  | { kind: "committed"; value: CommitProfileResponse }
  | { kind: "conflict"; conflict: ProfileRevisionConflict };

export type ProfileCandidateResolution =
  | { action: "keep_canonical" }
  | { action: "promote_candidate" }
  | { action: "copy_to_new_context"; name: string };

export interface ResolveProfileCandidateResponse {
  candidate_id: string;
  action: ProfileCandidateResolution["action"];
  context_id: string;
  revision: number | null;
}

const CONTEXT_KEYS = new Set([
  "id",
  "name",
  "browser",
  "current_revision",
  "profile_size_bytes",
  "status",
  "created_at",
  "updated_at",
  "session_count",
  "active_session_count",
  "last_saved_at",
  "vault_status",
  "vault_error",
  "vault_retryable",
]);

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isContextStatus(value: unknown): value is BrowserContextStatus {
  return value === "ready" || value === "syncing" || value === "error";
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
      value,
    )
  );
}

function isDateTime(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

/** Strict runtime guard for the owner-free public context DTO. */
export function isBrowserContextSummary(
  value: unknown,
): value is BrowserContextSummary {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if ("owner_id" in obj || "session_token" in obj) return false;
  if (Object.keys(obj).some((key) => !CONTEXT_KEYS.has(key))) return false;

  return (
    isUuid(obj.id) &&
    typeof obj.name === "string" &&
    obj.name.trim().length > 0 &&
    (obj.browser === "chrome" || obj.browser === "firefox") &&
    (obj.vault_status === undefined ? obj.browser === "chrome" :
      ["pending", "ready", "error"].includes(String(obj.vault_status))) &&
    (obj.vault_error === undefined || obj.vault_error === null || typeof obj.vault_error === "string") &&
    (obj.vault_retryable === undefined ? obj.browser === "chrome" : typeof obj.vault_retryable === "boolean") &&
    isNonNegativeInteger(obj.current_revision) &&
    isNonNegativeInteger(obj.profile_size_bytes) &&
    isContextStatus(obj.status) &&
    isDateTime(obj.created_at) &&
    isDateTime(obj.updated_at) &&
    isNonNegativeInteger(obj.session_count) &&
    isNonNegativeInteger(obj.active_session_count) &&
    obj.active_session_count <= obj.session_count &&
    (obj.last_saved_at === null || isDateTime(obj.last_saved_at))
  );
}

export function isBrowserContextSummaryList(
  value: unknown,
): value is BrowserContextSummary[] {
  return Array.isArray(value) && value.every(isBrowserContextSummary);
}

export function formatProfileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

export function formatContextDate(value: string | null): string {
  if (value === null) return "Jamais";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date indisponible";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function formatContextStatus(status: BrowserContextStatus): string {
  switch (status) {
    case "ready":
      return "Prêt";
    case "syncing":
      return "Synchronisation";
    case "error":
      return "Erreur";
  }
}
